"use client";

import { useLogger } from "@/lib/observability/LoggerContext";
import { Logo } from "@workspace/brand/components/logo";
import { Button } from "@workspace/ui/components/button";
import { useEffect } from "react";

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const logger = useLogger("Error");

  useEffect(() => {
    logger.logError("Error", error, {
      digest: error.digest,
      userAgent:
        typeof window !== "undefined" ? window.navigator.userAgent : undefined,
    });
  }, [error, logger]);

  return (
    <div className="min-h-screen bg-cream">
      {/* Hero Background Section */}
      <section className="relative w-full overflow-hidden mb-6 h-[368px]">
        {/* White Background */}
        <div className="absolute inset-0 bg-brand-100" />

        {/* Static Background with Lower Opacity */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-90"
          style={{
            backgroundImage:
              "url('https://localplatform.fly.storage.tigris.dev/assets/liquid_hero_first_frame.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />

        {/* Top Gradient Fade from Cream Background */}
        <div className="absolute top-0 left-0 right-0 h-24 bg-linear-to-b from-cream via-cream/70 to-transparent pointer-events-none" />

        {/* Bottom Gradient Fade to Cream Background */}
        <div className="absolute bottom-0 left-0 right-0 h-36 bg-linear-to-t from-cream via-cream/70 to-transparent pointer-events-none" />
      </section>

      {/* Content Container */}
      <div className="relative z-10 flex flex-col items-center justify-center px-4 py-6 md:py-8 md:px-8 -mt-64">
        {/* Title section */}
        <div className="mx-auto max-w-2xl text-center w-full flex flex-col items-center gap-4 mb-6">
          <Logo symbolOnly className="size-10 md:size-12" />
          <div className="flex flex-col items-center gap-2">
            <h1 className="text-3xl md:text-4xl font-bold font-serif mb-0">
              Something went wrong
            </h1>
            <p className="text-sm md:text-base text-brand-700 max-w-md">
              We&apos;re sorry, but something unexpected happened. Our team has
              been notified and is working to fix the issue.
            </p>
          </div>
        </div>

        {/* Content Card */}
        <div className="shadow-md backdrop-blur-xl bg-cream-50/70 rounded-lg px-4 md:px-8 py-6 md:py-8 w-full md:w-[500px] border border-neutral-200/50 mb-24">
          <div className="flex flex-col items-center gap-6">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-brand-900 mb-2">
                Error Details
              </h2>
              <p className="text-sm text-brand-600 mb-4">
                {process.env.NODE_ENV === "development"
                  ? error.message || "An unexpected error occurred"
                  : "An unexpected error occurred"}
              </p>
              {error.digest && (
                <p className="text-xs text-brand-500 font-mono">
                  Error ID: {error.digest}
                </p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <Button onClick={reset} variant="default" className="flex-1">
                Try Again
              </Button>
              <Button
                onClick={() => (window.location.href = "/")}
                variant="outline"
                className="flex-1"
              >
                Go Home
              </Button>
            </div>

            <div className="text-center">
              <p className="text-xs text-brand-500">
                If this problem persists, please contact support or try
                refreshing the page.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
